<section class="page-shell">
  <header class="page-header">
    <div>
      <p class="eyebrow">
        <a [routerLink]="appRoutePaths.controleGestion.coutsExPost">Coûts ex-post</a>
      </p>
      <h2>Campagne {{ detail()?.campaign?.year }}</h2>
      @if (detail(); as campaignDetail) {
        <p class="hero-subtitle">
          Fichier « {{ campaignDetail.campaign.fileName }} » importé par
          {{ campaignDetail.campaign.createdBy }} le {{ formatDate(campaignDetail.campaign.createdAtUtc) }}.
        </p>
      }
    </div>

    <div class="header-actions">
      @if (detail(); as campaignDetail) {
        <oui-tag
          type="status"
          [status]="campaignDetail.campaign.statusLevel">
          {{ getCampaignStatusLabel() }}
        </oui-tag>
      }
      <oui-button
        type="outlined"
        iconSuffix="sync"
        [disabled]="loading()"
        (click)="reload()">
        Actualiser
      </oui-button>
      @if (detail()) {
        <oui-button
          type="outlined"
          iconSuffix="delete"
          [disabled]="!canDeleteCampaign()"
          ouiTooltip="Suppression définitive de la campagne (environnements de test uniquement)."
          (click)="deleteCampaign()">
          @if (deletingCampaign()) {
            Suppression…
          } @else {
            Supprimer
          }
        </oui-button>
      }
    </div>
  </header>

  @if (loading() && !detail()) {
    <dbw-state-card
      title="Chargement"
      appearance="loading">
      <p>Lecture de la campagne coûts ex-post.</p>
    </dbw-state-card>
  } @else if (error() && !detail()) {
    <dbw-state-card
      title="Erreur"
      appearance="error">
      <p>{{ error() }}</p>
    </dbw-state-card>
  } @else {
    @if (detail(); as campaignDetail) {
    <section class="content-grid">
      <dbw-summary-grid [items]="summaryItems()"></dbw-summary-grid>

      <article class="panel dbw-card dbw-card--xl">
        <div class="panel-heading">
          <h4>Actions</h4>
        </div>

        <div class="actions-row">
          <oui-button
            iconSuffix="picture_as_pdf"
            [disabled]="!canGenerate()"
            (click)="startGeneration()">
            @if (campaignDetail.campaign.status === 'Generating') {
              Génération en cours…
            } @else if (campaignDetail.campaign.status === 'GeneratedWithErrors') {
              Relancer la génération (erreurs)
            } @else {
              Lancer la génération des bordereaux
            }
          </oui-button>

          <span [ouiTooltip]="sendBatchTooltip()">
            <oui-button
              iconSuffix="outgoing_mail"
              [disabled]="!canSendBatch()"
              (click)="sendNextBatch()">
              @if (hasSendingBatch()) {
                Envoi du lot en cours…
              } @else {
                @if (nextPlannedBatch(); as batch) {
                  Envoyer le lot n°{{ batch.batchNumber }} ({{ batch.plannedCount }} emails)
                } @else {
                  Tous les lots sont envoyés
                }
              }
            </oui-button>
          </span>

          @if (canDownload()) {
            <span [ouiTooltip]="downloadTooltip()">
              <oui-button
                type="outlined"
                iconSuffix="folder_zip"
                [disabled]="!canDownloadDocuments() || downloading() !== null || campaignDetail.generatedCount === 0"
                (click)="downloadArchive('Email')">
                @if (downloading() === 'Email') {
                  Préparation du ZIP…
                } @else {
                  Télécharger les PDF mail
                }
              </oui-button>
            </span>

            <span [ouiTooltip]="downloadTooltip()">
              <oui-button
                type="outlined"
                iconSuffix="folder_zip"
                [disabled]="!canDownloadDocuments() || downloading() !== null || campaignDetail.generatedCount === 0"
                (click)="downloadArchive('Courrier')">
                @if (downloading() === 'Courrier') {
                  Préparation du ZIP…
                } @else {
                  Télécharger les PDF courrier
                }
              </oui-button>
            </span>

            <span [ouiTooltip]="downloadTooltip()">
              <oui-button
                type="outlined"
                iconSuffix="print"
                [disabled]="!canDownloadDocuments() || printingCourrier() || campaignDetail.courrierDocumentCount === 0"
                (click)="printCourrierDocuments()">
                @if (printingCourrier()) {
                  Préparation de l'impression…
                } @else {
                  Imprimer les courriers
                }
              </oui-button>
            </span>
          }
        </div>

        @if (quotaBlocksNextBatch()) {
          <p class="delay-hint">
            Quota SMTP atteint :
            <strong>{{ campaignDetail.emailQuota.sentInWindow }}</strong> /
            {{ campaignDetail.emailQuota.limit }} emails envoyés sur les dernières 24 h. Ce lot en
            demande {{ campaignDetail.emailQuota.nextBatchSize }}, il en reste
            {{ campaignDetail.emailQuota.remaining }} — prochain envoi possible le
            <strong>{{ formatDate(campaignDetail.emailQuota.nextBatchAllowedAtUtc) }}</strong>.
          </p>
        }
      </article>

      <section class="dbw-card dbw-card--xl table-card">
        <div class="table-card__header">
          <div>
            <h5>Lots d'envoi des emails</h5>
            <p>Relance manuelle, 24h minimum entre deux lots.</p>
          </div>
        </div>

        @if (campaignDetail.emailBatches.length === 0) {
          <div class="empty-state">Aucun lot d'envoi : aucun investisseur avec email dans cette campagne.</div>
        } @else {
          <div class="table-shell">
            <oui-table
              class="data-table"
              [lineSizing]="'dense'">
              <table
                mat-table
                [dataSource]="campaignDetail.emailBatches">
                <ng-container matColumnDef="batchNumber">
                  <th mat-header-cell *matHeaderCellDef>Lot</th>
                  <td mat-cell *matCellDef="let row"><strong>n°{{ row.batchNumber }}</strong></td>
                </ng-container>

                <ng-container matColumnDef="plannedCount">
                  <th mat-header-cell *matHeaderCellDef>Emails prévus</th>
                  <td mat-cell *matCellDef="let row">{{ row.plannedCount }}</td>
                </ng-container>

                <ng-container matColumnDef="progress">
                  <th mat-header-cell *matHeaderCellDef>Progression</th>
                  <td mat-cell *matCellDef="let row">
                    {{ row.sentCount }} envoyé(s)
                    @if (row.failedCount > 0) {
                      — <span class="error-text">{{ row.failedCount }} échec(s)</span>
                    }
                  </td>
                </ng-container>

                <ng-container matColumnDef="status">
                  <th mat-header-cell *matHeaderCellDef>Statut</th>
                  <td mat-cell *matCellDef="let row">
                    <oui-tag
                      type="status"
                      [status]="row.statusLevel">
                      {{ getBatchStatusLabel(row) }}
                    </oui-tag>
                  </td>
                </ng-container>

                <ng-container matColumnDef="startedAt">
                  <th mat-header-cell *matHeaderCellDef>Démarré le</th>
                  <td mat-cell *matCellDef="let row">{{ formatDate(row.startedAtUtc) }}</td>
                </ng-container>

                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef></th>
                  <td mat-cell *matCellDef="let row">
                    @if (isBatchResendable(row)) {
                      <oui-button
                        type="outlined"
                        iconSuffix="forward_to_inbox"
                        [disabled]="!canResendBatch(row)"
                        ouiTooltip="Renvoie les emails en échec ou jamais partis de ce lot. Les emails déjà envoyés ne sont pas renvoyés."
                        (click)="resendBatch(row)">
                        @if (resendingBatchId() === row.id) {
                          Relance…
                        } @else {
                          Relancer
                        }
                      </oui-button>
                    }
                  </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="batchColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: batchColumns"></tr>
              </table>
            </oui-table>
          </div>
        }
      </section>

      @if ((errorDocuments()?.totalCount ?? 0) > 0) {
        <section class="dbw-card dbw-card--xl table-card">
          <div class="table-card__header">
            <div>
              <h5>Documents en erreur</h5>
              <p>{{ errorDocuments()?.totalCount }} document(s) en échec de génération ou d'envoi.</p>
            </div>
          </div>

          <div class="table-shell">
            <oui-table
              class="data-table"
              [lineSizing]="'dense'">
              <table
                mat-table
                [dataSource]="errorDocuments()?.items ?? []">
                <ng-container matColumnDef="client">
                  <th mat-header-cell *matHeaderCellDef>Investisseur</th>
                  <td mat-cell *matCellDef="let row">
                    <div class="metric-stack">
                      <strong>{{ row.clientName }}</strong>
                      <span>{{ row.clientCode }}</span>
                    </div>
                  </td>
                </ng-container>

                <ng-container matColumnDef="consultant">
                  <th mat-header-cell *matHeaderCellDef>Consultant</th>
                  <td mat-cell *matCellDef="let row">{{ row.consultantName }}</td>
                </ng-container>

                <ng-container matColumnDef="deliveryMode">
                  <th mat-header-cell *matHeaderCellDef>Mode</th>
                  <td mat-cell *matCellDef="let row">
                    {{ row.deliveryMode === 'Email' ? 'Mail' : 'Courrier' }}
                  </td>
                </ng-container>

                <ng-container matColumnDef="status">
                  <th mat-header-cell *matHeaderCellDef>Statut</th>
                  <td mat-cell *matCellDef="let row">
                    <oui-tag
                      type="status"
                      [status]="row.statusLevel">
                      {{ row.status === 'GenerationFailed' ? 'Échec génération' : 'Échec envoi' }}
                    </oui-tag>
                  </td>
                </ng-container>

                <ng-container matColumnDef="errorMessage">
                  <th mat-header-cell *matHeaderCellDef>Erreur</th>
                  <td mat-cell *matCellDef="let row">{{ row.errorMessage }}</td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="errorColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: errorColumns"></tr>
              </table>
            </oui-table>

            <mat-paginator
              [length]="errorDocuments()?.totalCount ?? 0"
              [pageIndex]="errorDocuments()?.pageIndex ?? errorPageIndex()"
              [pageSize]="errorDocuments()?.pageSize ?? errorPageSize()"
              [pageSizeOptions]="pageSizeOptions"
              [showFirstLastButtons]="true"
              (page)="onErrorPageChange($event)">
            </mat-paginator>
          </div>
        </section>
      }

      @if (campaignDetail.importWarnings.length > 0) {
        <dbw-state-card
          appearance="info"
          title="Avertissements d'import"
          [compact]="true">
          <div class="issues-list">
            @for (issue of campaignDetail.importWarnings.slice(0, 50); track $index) {
              <p class="issue-item">
                <oui-tag
                  type="status"
                  [status]="issue.severity === 'Error' ? 'error' : 'pending'">
                  {{ issue.severity === 'Error' ? 'Erreur' : 'Avertissement' }}
                </oui-tag>
                <span>
                  @if (issue.sheet) {
                    [{{ issue.sheet }}@if (issue.rowNumber > 0) { — ligne {{ issue.rowNumber }}}]
                  }
                  {{ issue.message }}
                </span>
              </p>
            }
            @if (campaignDetail.importWarnings.length > 50) {
              <p class="issue-item">… et {{ campaignDetail.importWarnings.length - 50 }} autre(s).</p>
            }
          </div>
        </dbw-state-card>
      }
    </section>
    }
  }
</section>
